import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isIconAccessoryProp } from '#pplx/frontend/aether/components/Accessory/isIconAccessoryProp.js';
import { getButtonClasses } from '#pplx/frontend/aether/components/Button/classes.js';
import { LoadingOverlay } from '#pplx/frontend/aether/components/Button/LoadingOverlay.js';
import { getLoadingTransitionStyle } from '#pplx/frontend/aether/components/Button/loadingTransitionStyle.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { useMeasureableHandle } from '#pplx/frontend/aether/internal/useMeasureableHandle.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import { useTrackOnClick } from '#pplx/frontend/aether/internal/useTrackOnClick.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { asAutoInstrumented } from '#pplx/frontend/framework/instrumentation/types.js';
import classNames from 'classnames';
import React from 'react';
/** Module-level so `useTrackOnClick` deps stay stable. */
const BUTTON_DEFAULT_TRACK_OPTIONS = {
    event: 'button selected',
    data: asAutoInstrumented(),
};
/**
 * A versatile button component supporting multiple variants, sizes, icons,
 * and loading states. All icon buttons automatically get a Tooltip wrapper.
 *
 * @category components
 * @keywords button, components, versatile, component, supporting, multiple,
 *   variants, sizes, icons, loading, states, icon, buttons, automatically,
 *   tooltip, wrapper, disabled, isloading, leadingicon, trailingicon, link,
 *   spinner, chip, badge
 * @whenToUse Use Button for interactive actions that do not navigate to a new
 *   URL. It supports four visual variants (primary for main CTAs, secondary
 *   for supporting actions, tonal for subtle alternatives, and text for
 *   minimal emphasis), four sizes, icon-only and text+icon combinations,
 *   loading states, and inline placement within text. Choose Button over Link
 *   when the interaction triggers a state change, submits a form, or performs
 *   an in-page operation rather than navigating.
 * @guidelineDo
 *   - Always use Button as the default choice for interactive button elements — it is the core
 *     component that provides variants, sizes, icons, loading states, and accessibility out of the
 *     box. Prefer Button over ButtonPrimitive in all standard cases
 *   - Use the appropriate variant (primary, secondary, tonal, text) to communicate the action's
 *     importance in the visual hierarchy
 *   - Provide an aria-label when using the icon-only button form — it is required by the type
 *     system and drives the automatic Tooltip content by default
 *   - Use tooltipContent only when an icon button needs a concise visible Tooltip while its
 *     aria-label includes additional context that distinguishes the action for assistive technology
 *   - Use the isLoading prop to indicate pending operations — it shows a spinner overlay and
 *     prevents click handlers from firing
 *   - Use leadingAccessory and trailingAccessory props on text buttons to pair icons with labels,
 *     rather than placing Icon components inside children
 *   - Set the interactableVariant to 'alt' when the button is in a confined space where the
 *     default focus ring would clip
 *   - Use the inline prop on text-variant buttons inside containers (e.g. cards) where you need
 *     the label to align flush with surrounding text — it removes side padding and uses a
 *     text-color-only hover instead of a background highlight
 * @guidelineDont
 *   - Don't reach for ButtonPrimitive when Button can do the job — Button covers primary,
 *     secondary, tonal, and text variants with icon-only and text+icon combinations. Only use
 *     ButtonPrimitive as a last resort for truly custom one-off implementations
 *   - Don't use the text variant with isLoading — text buttons disallow the isLoading prop by
 *     design since they lack a visible container for the spinner
 *   - Don't set fullWidth, leadingAccessory, trailingAccessory, or pill on icon-only buttons —
 *     these props are restricted to text button variants via the type system
 *   - Don't combine rounded with pill or inline — rounded is exclusive to icon-only buttons,
 *     while pill and inline apply to text buttons only
 *   - Don't rely solely on the disabled visual state to communicate why a button is unavailable —
 *     disabled icon buttons do not render a Tooltip, so provide context elsewhere in the UI
 *   - Don't use inline for embedding buttons mid-sentence within a paragraph — inline is for
 *     aligning button labels flush with other content in a structured container like a card
 *   - Don't use Button for filter/tag toggles — use Chip when the element represents a selection
 *     that can be toggled on/off or removed, and Badge for non-interactive status labels
 * @accessibility
 *   - Icon-only buttons automatically render a Tooltip using tooltipContent when provided, or the
 *     aria-label text by default. The aria-label always remains the accessible name
 *   - When isLoading is true, the button sets aria-busy on the element, informing assistive
 *     technology that the action is in progress
 *   - The component defaults to type='button' to prevent accidental form submissions — set
 *     type='submit' explicitly when inside forms
 *   - Disabled icon buttons skip the Tooltip wrapper entirely since disabled elements are not
 *     focusable — ensure the disabled reason is communicated through other UI elements
 *   - The ButtonPrimitive provides the interactable focus ring with two variants: 'default'
 *     (standard offset ring) and 'alt' (inset ring for confined spaces)
 * @relatedComponents
 *   - icon
 *   - link
 *   - tooltip
 *   - spinner
 *   - chip
 *   - badge
 */
export function Button({ ref, children, icon, type, 'aria-label': ariaLabel, variant = 'primary', intent = 'neutral', size = 'default', disabled = false, isLoading = false, expanded, fullWidth = false, onClick, leadingAccessory, trailingAccessory, pill = false, rounded = false, inline = false, tooltipSide = 'top', tooltipAlign = 'center', tooltipContent, tooltipOffsetPx, tooltipDelayDurationMs, tooltipSubtitle, tooltipShouldShowArrow = false, tooltipShouldStayOpenOnClick = false, tooltipOpen, tooltipOnOpenChange, interactableVariant = 'default', track, __dangerousHtmlProps = {}, 
// We intentionally capture the rest of props that could be cloned/passed in by 3rd-party libraries
// such as Radix UI for their own internal usage such as data-state, onFocus, onBlur, etc.
...props }) {
    const targetRef = useMeasureableHandle(ref);
    // Manually pick the props that Radix UI passes in for their own internal usage
    // and pass them down to the ButtonPrimitive.
    const __radixProps = useRadixProps(props);
    const trackedOnClick = useTrackOnClick(track, BUTTON_DEFAULT_TRACK_OPTIONS, onClick);
    if (icon) {
        const buttonElement = (_jsx(ButtonPrimitive, { ref: targetRef, ...__radixProps, type: type, disabled: disabled, "aria-busy": isLoading || undefined, ...(expanded !== undefined ? { 'aria-expanded': expanded } : undefined), className: getButtonClasses({
                variant,
                intent,
                size,
                disabled,
                isLoading,
                fullWidth: false,
                rounded,
                pill: false,
                inline: false,
                iconOnly: true,
            }), onClick: isLoading ? undefined : trackedOnClick, "aria-label": ariaLabel, interactableVariant: interactableVariant, ...__dangerousHtmlProps, children: _jsxs("div", { className: "relative flex items-center justify-center", children: [_jsx("div", { style: getLoadingTransitionStyle(!isLoading), className: "inline-flex", children: _jsx(Icon, { icon: icon, size: size }) }), _jsx(LoadingOverlay, { size: size, variant: variant, isVisible: isLoading })] }) }));
        if (disabled) {
            // Disabled elements are not focusable so there's no need to wrap in a tooltip.
            // Consumers should apply some message elsewhere in the UI to indicate why the button is disabled.
            return buttonElement;
        }
        const isControlledTooltip = tooltipOpen !== undefined && tooltipOnOpenChange !== undefined;
        if (isControlledTooltip) {
            return (_jsx(Tooltip, { open: tooltipOpen, onOpenChange: tooltipOnOpenChange, content: tooltipContent ?? ariaLabel, subtitle: tooltipSubtitle, side: tooltipSide, align: tooltipAlign, offsetPx: tooltipOffsetPx, delayDurationMs: tooltipDelayDurationMs, shouldShowArrow: tooltipShouldShowArrow, shouldStayOpenOnClick: tooltipShouldStayOpenOnClick, children: buttonElement }));
        }
        return (_jsx(Tooltip, { content: tooltipContent ?? ariaLabel, subtitle: tooltipSubtitle, side: tooltipSide, align: tooltipAlign, offsetPx: tooltipOffsetPx, delayDurationMs: tooltipDelayDurationMs, shouldShowArrow: tooltipShouldShowArrow, shouldStayOpenOnClick: tooltipShouldStayOpenOnClick, children: buttonElement }));
    }
    return (_jsxs(ButtonPrimitive, { ref: targetRef, ...__radixProps, type: type, disabled: disabled, "aria-busy": isLoading || undefined, ...(expanded !== undefined ? { 'aria-expanded': expanded } : undefined), className: classNames(getButtonClasses({
            variant,
            intent,
            size,
            disabled,
            isLoading,
            fullWidth,
            rounded: false,
            pill,
            inline,
        }), 'relative'), onClick: isLoading ? undefined : trackedOnClick, "aria-label": ariaLabel, interactableVariant: interactableVariant, ...__dangerousHtmlProps, children: [leadingAccessory && (_jsx("div", { style: getLoadingTransitionStyle(!isLoading), className: classNames('flex shrink-0 items-center', 
                // Negative margin is to optically align the icon with the button's internal padding.
                '-ms-0.5'), children: isIconAccessoryProp(leadingAccessory) ? (_jsx(Icon, { icon: leadingAccessory, size: size })) : (leadingAccessory) })), _jsx("span", { style: getLoadingTransitionStyle(!isLoading), className: classNames(
                // Trims both the start/end of the text box (removes baked in vertical padding provided by the font itself)
                'text-box-trim-both', {
                    'min-w-0 truncate': fullWidth,
                    'ps-1': leadingAccessory,
                    'pe-1': trailingAccessory,
                }), children: children }), trailingAccessory && (_jsx("div", { style: getLoadingTransitionStyle(!isLoading), className: classNames('flex shrink-0 items-center', 
                // Negative margin is to optically align the icon with the button's internal padding.
                '-me-0.5'), children: isIconAccessoryProp(trailingAccessory) ? (_jsx(Icon, { icon: trailingAccessory, size: size })) : (trailingAccessory) })), _jsx(LoadingOverlay, { size: size, variant: variant, isVisible: isLoading })] }));
}
//# sourceMappingURL=index.js.map